'use client';
import { formatPhoneNumber } from '@utils/formatPhoneNumber';
import { Order } from '@repo/ui/src/types/order';
import OrderDetailPaymentInfo from './OrderDetailPaymentInfo';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';

export interface OrderDetailSummarySectionProps {
  orderDetails: Order['details'];
}

const OrderDetailCustomerSummary = ({
  orderDetails: { shipments, customerInfo, paymentInstruments, billingAddress },
}: OrderDetailSummarySectionProps) => {
  const { t } = useTranslation();
  const { shippingMethod, shippingAddress } = shipments?.[0] ?? {};
  return (
    <div>
      <h5 className="tra-text-web-heading-5 mb-5">
        {t('orderSummary.customerInformationLabel', undefined, {
          default: 'Customer Information',
        })}
      </h5>
      <div className="grid sm:grid-flow-col sm:grid-rows-[repeat(3,auto)] sm:grid-cols-2 sm:justify-start gap-6 p-6 bg-warm-100 border border-warm-300">
        <div>
          <p className="tra-text-web-body-m font-bold mb-2">
            {t('orderSummary.customerInformationLabel', undefined, {
              default: 'Customer Information',
            })}
          </p>
          {customerInfo?.email && (
            <p className="tra-text-web-body-m mb-0 break-words">{customerInfo.email}</p>
          )}
        </div>
        {shippingAddress && (
          <div>
            <p className="tra-text-web-body-m font-bold mb-2">
              {t('orderSummary.shippingAddressLabel', undefined, {
                default: 'Shipping Address',
              })}
            </p>
            <p className="tra-text-web-body-m mb-0 capitalize">
              {shippingAddress.fullName.toLowerCase()}
            </p>
            <p className="tra-text-web-body-m mb-0 capitalize">
              {shippingAddress.address1.toLowerCase()} {shippingAddress.address2?.toLowerCase()}
            </p>
            <p className="tra-text-web-body-m mb-0 capitalize">
              {shippingAddress.city.toLowerCase()}, {shippingAddress.stateCode.toUpperCase()}{' '}
              {shippingAddress.postalCode}
            </p>
            {shippingAddress.phone && (
              <p className="tra-text-web-body-m mb-0">{formatPhoneNumber(shippingAddress.phone)}</p>
            )}
          </div>
        )}
        {shippingMethod && (
          <div>
            <p className="tra-text-web-body-m font-bold mb-2">
              {t('orderSummary.shippingMethodLabel', undefined, {
                default: 'Shipping Method',
              })}
            </p>
            <p className="tra-text-web-body-m mb-0 capitalize">
              {shippingMethod.name.toLowerCase()}
            </p>
            <p className="tra-text-web-body-m mb-0 capitalize">
              {shippingMethod.description.toLowerCase()}
            </p>
          </div>
        )}
        <OrderDetailPaymentInfo paymentInstruments={paymentInstruments} />
        {billingAddress && (
          <div>
            <p className="tra-text-web-body-m font-bold mb-2">
              {t('orderSummary.billingAddressLabel', undefined, {
                default: 'Billing Address',
              })}
            </p>
            <p className="tra-text-web-body-m mb-0 capitalize">
              {billingAddress.fullName.toLowerCase()}
            </p>
            <p className="tra-text-web-body-m mb-0 capitalize">
              {billingAddress.address1.toLowerCase()} {billingAddress.address2?.toLowerCase()}
            </p>
            <p className="tra-text-web-body-m mb-0 capitalize">
              {billingAddress.city.toLowerCase()}, {billingAddress.stateCode.toUpperCase()}{' '}
              {billingAddress.postalCode}
            </p>
            {billingAddress.phone && (
              <p className="tra-text-web-body-m mb-0">{formatPhoneNumber(billingAddress.phone)}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailCustomerSummary;
